import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import DateRangeFilter from '../../components/DateRangeFilter';

// Constants
const ITEMS_PER_PAGE = 25;

// Utility functions
const formatTotalDuration = (totalSeconds) => {
  if (totalSeconds === 0) return '0s';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  let durationString = '';
  if (hours > 0) durationString += `${hours}h `;
  if (minutes > 0) durationString += `${minutes}m `;
  if (seconds > 0 || durationString === '') durationString += `${seconds}s`;
  return durationString.trim();
};

const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 'N/A';
  const start = new Date(startTime);
  const end = new Date(endTime);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return 'N/A';
  const durationMs = end - start;
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes === 0 ? `${seconds}s` : `${minutes}m ${seconds}s`;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  const callDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (callDate >= today) return `Today ${callDate.toLocaleTimeString()}`;
  if (callDate >= yesterday) return `Yesterday ${callDate.toLocaleTimeString()}`;
  return callDate.toLocaleString();
};

const shortenId = (id, length = 8) => {
  if (!id) return 'N/A';
  return id.length <= length + 3 ? id : `${id.substring(0, length)}...`;
};

// Components
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="p-4 md:p-6 2xl:p-10">
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" role="alert">
      <strong className="font-semibold">Error:</strong>
      <span className="block sm:inline ml-1">{message}</span>
    </div>
  </div>
);

const StatsSummary = ({ totalCalls, formattedTotalDuration, totalSuccessfulCalls, totalFailedCalls }) => (
  <div className="mt-4 sm:mt-0 text-sm text-gray-600 dark:text-gray-400 space-y-1 sm:space-y-0 sm:space-x-4 sm:flex">
    <span>Total Calls: <span className="font-semibold text-gray-800 dark:text-gray-200">{totalCalls}</span></span>
    <span>Duration: <span className="font-semibold text-gray-800 dark:text-gray-200">{formattedTotalDuration}</span></span>
    <span>Successful: <span className="font-semibold text-green-600 dark:text-green-400">{totalSuccessfulCalls}</span></span>
    <span>Failed: <span className="font-semibold text-red-600 dark:text-red-400">{totalFailedCalls}</span></span>
  </div>
);

const CallsPage = () => {
  const [calls, setCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [currentSummary, setCurrentSummary] = useState('');
  const [currentCallForSummary, setCurrentCallForSummary] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [currentCallForDetails, setCurrentCallForDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [isLoadingAssistants, setIsLoadingAssistants] = useState(true);
  const [successFilter, setSuccessFilter] = useState('');
  const [bookingIdFilter, setBookingIdFilter] = useState('');
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  // Get unique end reasons from calls
  const getGroupedEndReason = (reason) => {
    if (!reason) return 'Unknown';
    
    // Customer did not answer
    if (reason === 'customer-busy' || reason === 'customer-did-not-answer') {
      return 'Customer did not answer';
    }
    
    // Call Ended Normally
    if (reason === 'customer-ended-call' || reason === 'assistant-ended-call') {
      return 'Call ended normally';
    }
    
    // Silence Timeout
    if (reason === 'silence-timeout') {
      return 'Silence timeout';
    }
    
    // Technical Error
    if (reason.includes('call.in-progress.error')) {
      return 'Technical error';
    }
    
    return reason;
  };

  // Filter calls based on selected filters
  const filteredCalls = calls.filter(call => {
    const matchesSuccess = !successFilter || 
      (successFilter === 'success' && call.analysis?.successEvaluation === 'true') ||
      (successFilter === 'failure' && call.analysis?.successEvaluation === 'false') ||
      (successFilter === 'unknown' && !call.analysis?.successEvaluation);
    const matchesBookingId = !bookingIdFilter || 
      (call.name && call.name.toLowerCase().includes(bookingIdFilter.toLowerCase()));
    return matchesSuccess && matchesBookingId;
  });

  // Update pagination to use filtered calls
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentCalls = filteredCalls.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCalls.length / ITEMS_PER_PAGE);

  // Update stats to use filtered calls
  const totalCalls = filteredCalls.length;
  let totalDurationSeconds = 0;
  let totalSuccessfulCalls = 0;
  let totalFailedCalls = 0;

  filteredCalls.forEach(call => {
    const startTime = call.startedAt ?? call.createdAt;
    if (startTime && call.endedAt) {
      const start = new Date(startTime);
      const end = new Date(call.endedAt);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end >= start) {
        totalDurationSeconds += Math.floor((end - start) / 1000);
      }
    }
    if (call.analysis?.successEvaluation === 'true') {
      totalSuccessfulCalls++;
    } else if (call.analysis?.successEvaluation === 'false') {
      totalFailedCalls++;
    }
  });

  const formattedTotalDuration = formatTotalDuration(totalDurationSeconds);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [successFilter, bookingIdFilter]);

  const openSummaryModal = (summaryText, call) => {
    setCurrentSummary(summaryText || 'No summary available.');
    setCurrentCallForSummary(call);
    setIsSummaryModalOpen(true);
  };

  const closeSummaryModal = () => {
    setIsSummaryModalOpen(false);
    setCurrentSummary('');
    setCurrentCallForSummary(null);
  };

  const handleSummaryModalBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeSummaryModal();
    }
  };

  const openDetailsModal = (call) => {
    setCurrentCallForDetails(call);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setCurrentCallForDetails(null);
  };

  const copyToClipboard = async (text, label) => {
    if (!navigator.clipboard) {
      console.error('Clipboard API not available.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      console.log(`${label} copied to clipboard: ${text}`);
    } catch (err) {
      console.error(`Failed to copy ${label}: `, err);
    }
  };

  // Fetch assistants from Supabase
  useEffect(() => {
    const fetchAssistants = async () => {
      setIsLoadingAssistants(true);
      try {
        console.log('Fetching user...');
        // Get the current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) {
          console.error('Error getting user:', userError);
          throw userError;
        }
        if (!user) {
          console.error('No user found');
          setError('Not authenticated. Please log in.');
          return;
        }
        console.log('User found:', user.id);

        // First get the user's organization memberships
        console.log('Fetching organization memberships...');
        const { data: memberships, error: membershipsError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id);

        if (membershipsError) {
          console.error('Error fetching organization memberships:', membershipsError);
          throw membershipsError;
        }
        
        console.log('Organization memberships response:', memberships);
        
        if (!memberships || memberships.length === 0) {
          console.error('No organization memberships found for user');
          setError('No organization found. Please contact your administrator.');
          return;
        }

        // Get all assistants for the organization
        console.log('Fetching assistants for organization:', memberships[0].organization_id);
        const { data: assistantsData, error: assistantsError } = await supabase
          .from('organization_assistants')
          .select(`
            assistant_id,
            organizations (
              name
            )
          `)
          .eq('organization_id', memberships[0].organization_id);

        if (assistantsError) {
          console.error('Error fetching assistants:', assistantsError);
          throw assistantsError;
        }

        console.log('Assistants response:', assistantsData);

        if (!assistantsData || assistantsData.length === 0) {
          console.error('No assistants found for organization');
          setError('No assistants found for your organization.');
          return;
        }

        // Map the assistants data
        const assistantsList = assistantsData.map(a => ({
          id: a.assistant_id,
          organizationName: a.organizations?.name || 'Unknown Organization'
        }));

        console.log('Processed assistants list:', assistantsList);
        setAssistants(assistantsList);
        if (assistantsList.length > 0) {
          setSelectedAssistant(assistantsList[0].id);
        }
      } catch (error) {
        console.error('Error in fetchAssistants:', error);
        setError('Failed to fetch assistants: ' + error.message);
      } finally {
        setIsLoadingAssistants(false);
      }
    };

    fetchAssistants();
  }, []);

  useEffect(() => {
    const fetchCalls = async () => {
      if (!startDate || !endDate || !selectedAssistant) {
        console.log('Missing required data:', { startDate, endDate, selectedAssistant });
        return;
      }

      setIsLoading(true);
      setError(null);
      try {

        // Get the current user for organization access
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) {
          throw new Error('Authentication error: ' + userError.message);
        }
        if (!user) {
          throw new Error('Not authenticated. Please log in.');
        }

        // Get user's organization
        const { data: memberships, error: membershipsError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id);

        if (membershipsError) {
          throw new Error('Failed to get organization: ' + membershipsError.message);
        }
        
        if (!memberships || memberships.length === 0) {
          throw new Error('No organization found. Please contact your administrator.');
        }

        const organizationId = memberships[0].organization_id;

        // Build the date range for the query
        const startDateISO = new Date(startDate);
        startDateISO.setHours(0, 0, 0, 0);
        
        const endDateISO = new Date(endDate);
        endDateISO.setHours(23, 59, 59, 999);

        console.log('Fetching calls from database:', { 
          organizationId, 
          assistantId: selectedAssistant, 
          startDate: startDateISO.toISOString(), 
          endDate: endDateISO.toISOString() 
        });

        // Query calls from the database
        const { data: callsData, error: callsError } = await supabase
          .from('calls')
          .select(`
            id,
            vapi_call_id,
            assistant_id,
            name,
            status,
            customer_number,
            customer_name,
            customer_email,
            created_at_vapi,
            started_at_vapi,
            ended_at_vapi,
            ended_reason,
            cost,
            cost_breakdown,
            analysis,
            artifact,
            metadata
          `)
          .eq('organization_id', organizationId)
          .eq('assistant_id', selectedAssistant)
          .gte('created_at_vapi', startDateISO.toISOString())
          .lte('created_at_vapi', endDateISO.toISOString())
          .order('created_at_vapi', { ascending: false });

        if (callsError) {
          throw new Error('Failed to fetch calls: ' + callsError.message);
        }

        console.log('Calls data received from database:', callsData);
        
        // Debug: Log the first call's artifact structure to understand the data
        if (callsData && callsData.length > 0) {
          console.log('First call artifact structure:', callsData[0].artifact);
          console.log('First call metadata:', callsData[0].metadata);
        }
        
        // Transform database data to match the expected format
        const transformedCalls = (callsData || []).map(call => ({
          id: call.vapi_call_id, // Use vapi_call_id as the main ID for compatibility
          assistantId: call.assistant_id,
          name: call.name,
          status: call.status,
          customer: {
            number: call.customer_number,
            name: call.customer_name,
            email: call.customer_email
          },
          createdAt: call.created_at_vapi,
          startedAt: call.started_at_vapi,
          endedAt: call.ended_at_vapi,
          endedReason: call.ended_reason,
          cost: call.cost,
          costBreakdown: call.cost_breakdown,
          analysis: call.analysis,
          artifact: call.artifact,
          // Extract recording URL from artifact if available
          recordingUrl: call.artifact?.recording?.stereoUrl || null,
          // Extract metadata fields
          ...call.metadata
        }));

        setCalls(transformedCalls);


      } catch (e) {
        console.error("Failed to fetch calls:", e);
        setError(e.message);
        setCalls([]);
      }
      setIsLoading(false);
    };

    fetchCalls();
  }, [startDate, endDate, selectedAssistant]);

  if (isLoadingAssistants) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!assistants.length) return <ErrorMessage message="No assistants available." />;
  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="p-4 md:p-6 2xl:p-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-2">
          <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
            Calls
          </h1>
          <DateRangeFilter
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
          <select
            value={successFilter}
            onChange={(e) => setSuccessFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark text-gray-700 dark:text-gray-300"
          >
            <option value="">All Status</option>
            <option value="success">Successful</option>
            <option value="failure">Failed</option>
            <option value="unknown">Unknown</option>
          </select>
          <input
            type="text"
            placeholder="Filter by Booking ID"
            value={bookingIdFilter}
            onChange={(e) => setBookingIdFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark text-gray-700 dark:text-gray-300 min-w-[160px]"
          />
          {assistants.length > 1 && (
            <select
              value={selectedAssistant || ''}
              onChange={(e) => setSelectedAssistant(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark text-gray-700 dark:text-gray-300"
            >
              {assistants.map((assistant) => (
                <option key={assistant.id} value={assistant.id}>
                  {assistant.organizationName} - Assistant {assistant.id.substring(0, 8)}...
                </option>
              ))}
            </select>
          )}
        </div>
        {!isLoading && totalCalls > 0 && (
          <StatsSummary
            totalCalls={totalCalls}
            formattedTotalDuration={formattedTotalDuration}
            totalSuccessfulCalls={totalSuccessfulCalls}
            totalFailedCalls={totalFailedCalls}
          />
        )}
      </div>
      
      {totalCalls === 0 ? (
        <div className="bg-white dark:bg-boxdark shadow-md rounded-lg p-6">
          <p className="text-gray-700 dark:text-gray-300">No calls found.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-boxdark shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Booking</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Phone Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">End Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Success</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-boxdark divide-y divide-gray-200 dark:divide-gray-700">
                {currentCalls.map((call) => {
                  const createdAtText = formatDate(call.createdAt);
                  const durationText = formatDuration(call.startedAt ?? call.createdAt, call.endedAt);
                  const phoneNumberText = call.customer?.number || 'N/A';
                  const recordingAvailable = call.recordingUrl && call.recordingUrl !== 'null' && call.recordingUrl !== 'undefined' && call.recordingUrl !== '';
                  let successText = 'N/A';
                  if (call.analysis?.successEvaluation === 'true') {
                    successText = 'Yes';
                  } else if (call.analysis?.successEvaluation === 'false') {
                    successText = 'No';
                  }

                  return (
                    <tr key={call.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        <span 
                          onClick={() => { 
                            copyToClipboard(call.id, 'Call ID'); 
                            openDetailsModal(call); 
                          }}
                          className="cursor-pointer underline"
                          title={call.id}
                        >
                          {shortenId(call.id)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {call.name || 'N/A'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${createdAtText === 'N/A' ? '' : 'text-gray-500 dark:text-gray-300'}`}>
                        {createdAtText}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${durationText === 'N/A' ? '' : 'text-gray-500 dark:text-gray-300'}`}>
                        {durationText}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${phoneNumberText === 'N/A' ? '' : 'text-gray-500 dark:text-gray-300'}`}>
                        {phoneNumberText}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {getGroupedEndReason(call.endedReason)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {successText === 'N/A' ? 
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200">
                            N/A
                          </span> : 
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            successText === 'Yes' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {successText}
                          </span>
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex space-x-3">
                          {recordingAvailable && (
                            <button 
                              onClick={() => {
                                console.log('Recording button clicked, URL:', call.recordingUrl);
                                console.log('Full call object:', call);
                                if (call.recordingUrl && call.recordingUrl !== 'null' && call.recordingUrl !== 'undefined') {
                                  window.open(call.recordingUrl, '_blank');
                                } else {
                                  console.error('Invalid recording URL:', call.recordingUrl);
                                  alert('Recording URL not available');
                                }
                              }} 
                              className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-200"
                              title="Listen to Recording"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                          {call.analysis?.summary && (
                            <button 
                              onClick={() => openSummaryModal(call.analysis.summary, call)} 
                              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 bg-gray-100 dark:bg-gray-700 p-1 rounded"
                              title="View Summary"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {totalPages > 1 && (
            <div className="bg-white dark:bg-boxdark px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-boxdark hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-boxdark hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastItem, calls.length)}
                    </span>{' '}
                    of <span className="font-medium">{calls.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-indigo-50 dark:bg-indigo-900 border-indigo-500 dark:border-indigo-600 text-indigo-600 dark:text-indigo-300'
                            : 'bg-white dark:bg-boxdark border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {isSummaryModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out"
          onClick={handleSummaryModalBackdropClick}
        >
          <div className="bg-white dark:bg-boxdark p-6 rounded-lg shadow-xl max-w-lg w-full mx-4 transform transition-all duration-300 ease-in-out scale-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Call Summary</h3>
              <button 
                onClick={closeSummaryModal} 
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            {currentCallForSummary && (
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                <div className="text-sm">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">Call ID: </span>
                  <span 
                    onClick={() => copyToClipboard(currentCallForSummary.id, 'Call ID')}
                    className="cursor-pointer underline text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 break-all"
                    title={currentCallForSummary.id}
                  >
                    {currentCallForSummary.id}
                  </span>
                </div>
              </div>
            )}
            <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
              {currentSummary}
            </div>
            <div className="mt-6 flex justify-end">
              <button 
                onClick={closeSummaryModal} 
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isDetailsModalOpen && currentCallForDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out">
          <div className="bg-white dark:bg-boxdark p-6 rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all duration-300 ease-in-out scale-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Call Details</h3>
              <button 
                onClick={closeDetailsModal} 
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
              <div>
                <span className="font-semibold text-gray-900 dark:text-gray-100">Call ID: </span>
                <span 
                  onClick={() => copyToClipboard(currentCallForDetails.id, 'Call ID')}
                  style={{cursor: 'pointer', textDecoration: 'underline'}}
                  title={currentCallForDetails.id}
                  className="break-all"
                >
                  {currentCallForDetails.id}
                </span>
              </div>
              <div>
                <span className="font-semibold text-gray-900 dark:text-gray-100">Assistant ID: </span>
                {currentCallForDetails.assistantId ? (
                  <span 
                    onClick={() => copyToClipboard(currentCallForDetails.assistantId, 'Assistant ID')}
                    style={{cursor: 'pointer', textDecoration: 'underline'}}
                    title={currentCallForDetails.assistantId}
                    className="break-all"
                  >
                    {currentCallForDetails.assistantId}
                  </span>
                ) : 'N/A'}
              </div>
              <div>
                <span className="font-semibold text-gray-900 dark:text-gray-100">Status: </span>
                <span>{currentCallForDetails.status || 'N/A'}</span>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button 
                onClick={closeDetailsModal} 
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallsPage;